/**
 * ProductEditor - Advanced Product Configuration
 * 
 * Tabbed interface for editing:
 * - Basic info (name, price, category)
 * - Variants (sizes, portions)
 * - Ingredients ("OHNE" options)
 * - Extras (add-ons)
 */

import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  Plus, Trash2, GripVertical, AlertCircle, Check, X,
  Package, Layers, Ban, Sparkles, ChefHat, Eye, EyeOff
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';

// Quick templates for common product types
const INGREDIENT_TEMPLATES = {
  burger: [
    { name: 'Brötchen', is_removable: false },
    { name: 'Zwiebeln', is_removable: true, show_prominent: true },
    { name: 'Tomaten', is_removable: true, show_prominent: true },
    { name: 'Gewürzgurken', is_removable: true, show_prominent: true },
    { name: 'Salat', is_removable: true, show_prominent: true },
    { name: 'Sauce', is_removable: true, show_prominent: false }
  ],
  pizza: [
    { name: 'Käse', is_removable: true, show_prominent: true },
    { name: 'Tomatensauce', is_removable: true, show_prominent: false },
    { name: 'Oregano', is_removable: true, show_prominent: false }
  ],
  wrap: [
    { name: 'Zwiebeln', is_removable: true, show_prominent: true },
    { name: 'Tomaten', is_removable: true, show_prominent: true },
    { name: 'Salat', is_removable: true, show_prominent: true },
    { name: 'Sauce', is_removable: true, show_prominent: false }
  ],
  salat: [
    { name: 'Zwiebeln', is_removable: true, show_prominent: true },
    { name: 'Gurken', is_removable: true, show_prominent: true },
    { name: 'Tomaten', is_removable: true, show_prominent: true },
    { name: 'Dressing', is_removable: true, show_prominent: false }
  ]
};

const VARIANT_TEMPLATES = {
  burger: [
    { name: 'Medium (125g)', price_mod: 0 },
    { name: 'Large (180g)', price_mod: 4 }
  ],
  pizza: [
    { name: '26 cm', price_mod: 0 },
    { name: '32 cm', price_mod: 4 }
  ],
  drink: [
    { name: '0,33l', price_mod: 0 },
    { name: '0,5l', price_mod: 1 }
  ],
  portion: [
    { name: 'Klein', price_mod: 0 },
    { name: 'Mittel', price_mod: 2 },
    { name: 'Groß', price_mod: 4 }
  ]
};

export default function ProductEditor({ 
  product, 
  isOpen, 
  onClose, 
  onSave,
  categories = []
}) {
  const [activeTab, setActiveTab] = useState('basic');
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    kitchen_name: '',
    description: '',
    category_id: '',
    price: '',
    tax_rate: 7,
    preparation_type: 'kitchen'
  });
  
  // Variants state
  const [variants, setVariants] = useState([]);
  const [hasVariants, setHasVariants] = useState(false);
  
  // Ingredients state (OHNE)
  const [ingredients, setIngredients] = useState([]);
  const [hasIngredients, setHasIngredients] = useState(false);
  
  // Extras state
  const [extraGroups, setExtraGroups] = useState([]);
  const [hasExtras, setHasExtras] = useState(false);
  
  // New ingredient input
  const [newIngredient, setNewIngredient] = useState('');
  const [newVariant, setNewVariant] = useState({ name: '', price: '' });
  const [newExtra, setNewExtra] = useState({ name: '', price: '' });

  // Load product data when opened
  useEffect(() => {
    if (product && isOpen) {
      setFormData({
        name: product.name || '',
        kitchen_name: product.kitchen_name || '',
        description: product.description || '',
        category_id: product.category_id || '',
        price: product.price?.toString() || product.base_price?.toString() || '',
        tax_rate: product.tax_rate || product.vat_rate || 7,
        preparation_type: product.preparation_type || 'kitchen'
      });
      
      setHasVariants(product.has_variants || false);
      setVariants(product.variants || []);
      
      setHasIngredients(product.has_removable_ingredients || false);
      setIngredients(product.ingredients || []);
      
      setHasExtras(product.has_extras || false);
      setExtraGroups(product.extra_groups || []);
    } else if (!product && isOpen) {
      // Reset for new product
      setFormData({
        name: '',
        kitchen_name: '',
        description: '',
        category_id: categories[0]?.category_id || '',
        price: '',
        tax_rate: 7,
        preparation_type: 'kitchen'
      });
      setVariants([]);
      setHasVariants(false);
      setIngredients([]);
      setHasIngredients(false);
      setExtraGroups([]);
      setHasExtras(false);
    }
  }, [product, isOpen, categories]);

  // Auto-generate kitchen_name from name
  useEffect(() => {
    if (formData.name && !formData.kitchen_name) {
      setFormData(prev => ({
        ...prev,
        kitchen_name: formData.name.toUpperCase().slice(0, 15)
      }));
    }
  }, [formData.name]);

  // === INGREDIENT HANDLERS ===
  const addIngredient = () => {
    if (!newIngredient.trim()) return;
    
    const ingredient = {
      ingredient_id: `temp-${Date.now()}`,
      name: newIngredient.trim(),
      short_name: newIngredient.trim().slice(0, 15),
      is_standard: true,
      is_removable: true,
      removal_label: `OHNE ${newIngredient.trim()}`,
      kitchen_label: newIngredient.trim().toUpperCase().slice(0, 15),
      show_prominent: true,
      allergen_ids: [],
      sort_order: ingredients.length
    };
    
    setIngredients([...ingredients, ingredient]);
    setNewIngredient('');
    setHasIngredients(true);
  };

  const updateIngredient = (index, field, value) => {
    const updated = [...ingredients];
    updated[index] = { ...updated[index], [field]: value };
    
    // Auto-update removal_label when name changes
    if (field === 'name') {
      updated[index].removal_label = `OHNE ${value}`;
      updated[index].kitchen_label = value.toUpperCase().slice(0, 15);
    }
    
    setIngredients(updated);
  };

  const removeIngredient = (index) => {
    const updated = ingredients.filter((_, i) => i !== index);
    setIngredients(updated);
    if (updated.length === 0) setHasIngredients(false);
  };

  const applyIngredientTemplate = (templateKey) => {
    const template = INGREDIENT_TEMPLATES[templateKey];
    if (!template) return;
    
    const newIngredients = template.map((t, idx) => ({
      ingredient_id: `temp-${Date.now()}-${idx}`,
      name: t.name,
      short_name: t.name.slice(0, 15),
      is_standard: true,
      is_removable: t.is_removable,
      removal_label: t.is_removable ? `OHNE ${t.name}` : null,
      kitchen_label: t.name.toUpperCase().slice(0, 15),
      show_prominent: t.show_prominent ?? true,
      allergen_ids: [],
      sort_order: idx
    }));
    
    setIngredients(newIngredients);
    setHasIngredients(true);
    toast.success(`${template.length} Zutaten aus Vorlage geladen`);
  };

  // === VARIANT HANDLERS ===
  const addVariant = () => {
    if (!newVariant.name.trim() || !newVariant.price) return;
    
    const variant = {
      variant_id: `temp-${Date.now()}`,
      name: newVariant.name.trim(),
      short_name: newVariant.name.trim().slice(0, 10),
      price: parseFloat(newVariant.price),
      is_default: variants.length === 0,
      sort_order: variants.length,
      active: true
    };
    
    setVariants([...variants, variant]);
    setNewVariant({ name: '', price: '' });
    setHasVariants(true);
  };

  const updateVariant = (index, field, value) => {
    const updated = [...variants];
    updated[index] = { ...updated[index], [field]: value };
    
    // Only one can be default
    if (field === 'is_default' && value === true) {
      updated.forEach((v, i) => {
        if (i !== index) v.is_default = false;
      });
    }
    
    setVariants(updated);
  };

  const removeVariant = (index) => {
    const updated = variants.filter((_, i) => i !== index);
    // Ensure at least one default
    if (updated.length > 0 && !updated.some(v => v.is_default)) {
      updated[0].is_default = true;
    }
    setVariants(updated);
    if (updated.length === 0) setHasVariants(false);
  };

  const applyVariantTemplate = (templateKey) => {
    const template = VARIANT_TEMPLATES[templateKey];
    if (!template) return;
    
    const basePrice = parseFloat(formData.price) || 0;
    const newVariants = template.map((t, idx) => ({
      variant_id: `temp-${Date.now()}-${idx}`,
      name: t.name,
      short_name: t.name.slice(0, 10),
      price: basePrice + (t.price_mod || 0),
      is_default: idx === 0,
      sort_order: idx,
      active: true
    }));
    
    setVariants(newVariants);
    setHasVariants(true);
    toast.success(`${template.length} Varianten aus Vorlage geladen`);
  };

  // === EXTRA HANDLERS ===
  const addExtra = () => {
    if (!newExtra.name.trim()) return;
    
    // Add to first group or create one
    if (extraGroups.length === 0) {
      const newGroup = {
        group_id: `temp-group-${Date.now()}`,
        name: 'Extras',
        type: 'addon',
        selection_type: 'multi',
        required: false,
        min_selections: 0,
        max_selections: 5,
        show_on_kitchen: true,
        extras: [{
          extra_id: `temp-${Date.now()}`,
          name: newExtra.name.trim(),
          short_name: `+${newExtra.name.trim().slice(0, 12)}`,
          price: parseFloat(newExtra.price) || 0,
          is_free: parseFloat(newExtra.price) === 0,
          sort_order: 0,
          active: true
        }],
        sort_order: 0
      };
      setExtraGroups([newGroup]);
    } else {
      const updated = [...extraGroups];
      updated[0].extras.push({
        extra_id: `temp-${Date.now()}`,
        name: newExtra.name.trim(),
        short_name: `+${newExtra.name.trim().slice(0, 12)}`,
        price: parseFloat(newExtra.price) || 0,
        is_free: parseFloat(newExtra.price) === 0,
        sort_order: updated[0].extras.length,
        active: true
      });
      setExtraGroups(updated);
    }
    
    setNewExtra({ name: '', price: '' });
    setHasExtras(true);
  };

  const removeExtra = (groupIndex, extraIndex) => {
    const updated = [...extraGroups];
    updated[groupIndex].extras = updated[groupIndex].extras.filter((_, i) => i !== extraIndex);
    
    // Remove empty groups
    const filtered = updated.filter(g => g.extras.length > 0);
    setExtraGroups(filtered);
    if (filtered.length === 0) setHasExtras(false);
  };

  // === SAVE HANDLER ===
  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Produktname ist erforderlich');
      return;
    }
    if (!formData.price || parseFloat(formData.price) < 0) {
      toast.error('Preis ist erforderlich');
      return;
    }
    
    setLoading(true);
    
    try {
      const productData = {
        name: formData.name.trim(),
        kitchen_name: formData.kitchen_name || formData.name.toUpperCase().slice(0, 15),
        description: formData.description,
        category_id: formData.category_id,
        price: parseFloat(formData.price),
        base_price: parseFloat(formData.price),
        tax_rate: parseInt(formData.tax_rate),
        vat_rate: parseInt(formData.tax_rate),
        preparation_type: formData.preparation_type,
        
        // Variants
        has_variants: hasVariants && variants.length > 0,
        variants: hasVariants ? variants : [],
        
        // Ingredients (OHNE)
        has_removable_ingredients: hasIngredients && ingredients.some(i => i.is_removable),
        ingredients: hasIngredients ? ingredients : [],
        
        // Extras
        has_extras: hasExtras && extraGroups.length > 0,
        extra_groups: hasExtras ? extraGroups : []
      };
      
      if (product?.product_id) {
        // Update existing
        await api.patch(`/api/products/${product.product_id}`, productData);
        toast.success('Produkt aktualisiert');
      } else {
        // Create new
        await api.post('/api/products', productData);
        toast.success('Produkt erstellt');
      }
      
      onSave?.();
      onClose();
    } catch (err) {
      toast.error(`Fehler: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // === KITCHEN BON PREVIEW ===
  const KitchenBonPreview = () => {
    const removableIngredients = ingredients.filter(i => i.is_removable);
    const extras = extraGroups.flatMap(g => g.extras);
    
    if (removableIngredients.length === 0 && extras.length === 0) {
      return (
        <div className="text-center text-gray-500 py-4">
          Fügen Sie Zutaten oder Extras hinzu, um eine Vorschau zu sehen
        </div>
      );
    }
    
    return (
      <div className="bg-white text-black p-4 rounded font-mono text-sm border-2 border-dashed">
        <div className="font-bold mb-2">
          {formData.kitchen_name || formData.name?.toUpperCase() || 'PRODUKT'}
          {variants.length > 0 && ` (${variants.find(v => v.is_default)?.short_name || variants[0]?.short_name})`}
        </div>
        
        {/* OHNE - Always on top, prominent */}
        {removableIngredients.slice(0, 3).map((ing, idx) => (
          <div 
            key={idx} 
            className={`${ing.show_prominent ? 'bg-gray-200 px-2 py-1 my-1 font-bold' : ''}`}
          >
            ❌ {ing.removal_label}
          </div>
        ))}
        
        {/* Separator */}
        {removableIngredients.length > 0 && extras.length > 0 && (
          <div className="border-t border-gray-300 my-2"></div>
        )}
        
        {/* Extras */}
        {extras.slice(0, 3).map((extra, idx) => (
          <div key={idx}>
            + {extra.name}
            {extra.price > 0 && ` (+€${extra.price.toFixed(2)})`}
          </div>
        ))}
        
        <div className="text-xs text-gray-500 mt-2">
          📋 Vorschau Küchenbon
        </div>
      </div>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            {product ? 'Produkt bearbeiten' : 'Neues Produkt'}
          </DialogTitle>
        </DialogHeader>
        
        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
          <TabsList className="grid grid-cols-4 mb-4">
            <TabsTrigger value="basic" className="flex items-center gap-1">
              <Package className="h-4 w-4" />
              Basis
            </TabsTrigger>
            <TabsTrigger value="variants" className="flex items-center gap-1">
              <Layers className="h-4 w-4" />
              Größen
              {hasVariants && variants.length > 0 && (
                <Badge variant="secondary" className="ml-1 h-5 w-5 p-0 justify-center">
                  {variants.length}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="ingredients" className="flex items-center gap-1">
              <Ban className="h-4 w-4" />
              OHNE
              {hasIngredients && ingredients.filter(i => i.is_removable).length > 0 && (
                <Badge variant="destructive" className="ml-1 h-5 w-5 p-0 justify-center">
                  {ingredients.filter(i => i.is_removable).length}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="extras" className="flex items-center gap-1">
              <Sparkles className="h-4 w-4" />
              Extras
              {hasExtras && extraGroups.length > 0 && (
                <Badge variant="secondary" className="ml-1 h-5 w-5 p-0 justify-center">
                  {extraGroups.reduce((sum, g) => sum + g.extras.length, 0)}
                </Badge>
              )}
            </TabsTrigger>
          </TabsList>
          
          <ScrollArea className="flex-1 pr-4">
            {/* === BASIC INFO TAB === */}
            <TabsContent value="basic" className="mt-0 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label>Produktname *</Label>
                  <Input
                    value={formData.name}
                    onChange={e => setFormData({ ...formData, name: e.target.value })}
                    placeholder="z.B. Double Cheeseburger"
                    data-testid="product-name-input"
                  />
                </div>
                
                <div>
                  <Label>Küchenname (max. 15 Zeichen)</Label>
                  <Input
                    value={formData.kitchen_name}
                    onChange={e => setFormData({ ...formData, kitchen_name: e.target.value.toUpperCase().slice(0, 15) })}
                    placeholder="DBL CHEESE"
                    maxLength={15}
                    data-testid="kitchen-name-input"
                  />
                  <p className="text-xs text-gray-500 mt-1">Wird auf Küchenbons angezeigt</p>
                </div>
                
                <div>
                  <Label>Kategorie</Label>
                  <select
                    value={formData.category_id}
                    onChange={e => setFormData({ ...formData, category_id: e.target.value })}
                    className="w-full h-10 px-3 rounded-md border border-input bg-background"
                    data-testid="category-select"
                  >
                    <option value="">Kategorie wählen</option>
                    {categories.map(cat => (
                      <option key={cat.category_id} value={cat.category_id}>
                        {cat.icon} {cat.name}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <Label>Preis (€) *</Label>
                  <Input
                    type="number"
                    step="0.10"
                    min="0"
                    value={formData.price}
                    onChange={e => setFormData({ ...formData, price: e.target.value })}
                    placeholder="9.90"
                    data-testid="price-input"
                  />
                </div>
                
                <div>
                  <Label>MwSt.</Label>
                  <select
                    value={formData.tax_rate}
                    onChange={e => setFormData({ ...formData, tax_rate: parseInt(e.target.value) })}
                    className="w-full h-10 px-3 rounded-md border border-input bg-background"
                    data-testid="tax-select"
                  >
                    <option value={7}>7% (Essen)</option>
                    <option value={19}>19% (Getränke/Vor Ort)</option>
                  </select>
                </div>
                
                <div className="col-span-2">
                  <Label>Beschreibung</Label>
                  <Input
                    value={formData.description}
                    onChange={e => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Optionale Beschreibung..."
                    data-testid="description-input"
                  />
                </div>
              </div>
            </TabsContent>
            
            {/* === VARIANTS TAB === */}
            <TabsContent value="variants" className="mt-0 space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Switch 
                    checked={hasVariants}
                    onCheckedChange={setHasVariants}
                    id="has-variants"
                  />
                  <Label htmlFor="has-variants">Hat Größen/Varianten</Label>
                </div>
                
                {hasVariants && (
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => applyVariantTemplate('burger')}
                    >
                      🍔 Burger
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => applyVariantTemplate('pizza')}
                    >
                      🍕 Pizza
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => applyVariantTemplate('drink')}
                    >
                      🥤 Getränk
                    </Button>
                  </div>
                )}
              </div>
              
              {hasVariants && (
                <>
                  {/* Existing variants */}
                  <div className="space-y-2">
                    {variants.map((variant, idx) => (
                      <div 
                        key={variant.variant_id} 
                        className={`flex items-center gap-2 p-3 rounded-lg border ${variant.is_default ? 'border-primary bg-primary/5' : 'border-border'}`}
                      >
                        <GripVertical className="h-4 w-4 text-gray-400" />
                        
                        <Input
                          value={variant.name}
                          onChange={e => updateVariant(idx, 'name', e.target.value)}
                          className="flex-1"
                          placeholder="Name"
                        />
                        
                        <div className="flex items-center gap-1">
                          <span className="text-sm text-gray-500">€</span>
                          <Input
                            type="number"
                            step="0.10"
                            value={variant.price}
                            onChange={e => updateVariant(idx, 'price', parseFloat(e.target.value))}
                            className="w-24"
                          />
                        </div>
                        
                        <Button
                          variant={variant.is_default ? "default" : "outline"}
                          size="sm"
                          onClick={() => updateVariant(idx, 'is_default', true)}
                          title="Als Standard setzen"
                        >
                          {variant.is_default ? <Check className="h-4 w-4" /> : 'Standard'}
                        </Button>
                        
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => removeVariant(idx)}
                          className="text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                  
                  {/* Add new variant */}
                  <div className="flex gap-2 pt-2 border-t">
                    <Input
                      value={newVariant.name}
                      onChange={e => setNewVariant({ ...newVariant, name: e.target.value })}
                      placeholder="Neue Variante (z.B. Large)"
                      className="flex-1"
                      onKeyPress={e => e.key === 'Enter' && addVariant()}
                    />
                    <Input
                      type="number"
                      step="0.10"
                      value={newVariant.price}
                      onChange={e => setNewVariant({ ...newVariant, price: e.target.value })}
                      placeholder="Preis"
                      className="w-24"
                      onKeyPress={e => e.key === 'Enter' && addVariant()}
                    />
                    <Button onClick={addVariant}>
                      <Plus className="h-4 w-4 mr-1" /> Hinzufügen
                    </Button>
                  </div>
                </>
              )}
            </TabsContent>
            
            {/* === INGREDIENTS (OHNE) TAB === */}
            <TabsContent value="ingredients" className="mt-0 space-y-4">
              <div className="bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 rounded-lg p-4 mb-4">
                <div className="flex items-start gap-2">
                  <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-amber-800 dark:text-amber-200">
                      "OHNE"-Optionen für die Küche
                    </p>
                    <p className="text-sm text-amber-700 dark:text-amber-300">
                      Diese Zutaten können vom Kunden abgewählt werden. 
                      "OHNE" erscheint <strong>groß und prominent</strong> auf dem Küchenbon.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Switch 
                    checked={hasIngredients}
                    onCheckedChange={setHasIngredients}
                    id="has-ingredients"
                  />
                  <Label htmlFor="has-ingredients">Hat abwählbare Zutaten</Label>
                </div>
                
                {hasIngredients && (
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => applyIngredientTemplate('burger')}
                    >
                      🍔 Burger
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => applyIngredientTemplate('pizza')}
                    >
                      🍕 Pizza
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => applyIngredientTemplate('wrap')}
                    >
                      🌯 Wrap
                    </Button>
                  </div>
                )}
              </div>
              
              {hasIngredients && (
                <>
                  {/* Existing ingredients */}
                  <div className="space-y-2">
                    {ingredients.map((ing, idx) => (
                      <div 
                        key={ing.ingredient_id} 
                        className={`flex items-center gap-2 p-3 rounded-lg border ${ing.is_removable ? 'border-red-200 bg-red-50 dark:bg-red-950/30 dark:border-red-900' : 'border-border'}`}
                      >
                        <GripVertical className="h-4 w-4 text-gray-400 cursor-move" />
                        
                        <Input
                          value={ing.name || ''}
                          onChange={e => updateIngredient(idx, 'name', e.target.value)}
                          className="flex-1 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                          placeholder="Zutat"
                        />
                        
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={ing.is_removable || false}
                            onCheckedChange={v => updateIngredient(idx, 'is_removable', v)}
                            id={`removable-${idx}`}
                          />
                          <Label htmlFor={`removable-${idx}`} className="text-sm whitespace-nowrap">
                            Abwählbar
                          </Label>
                        </div>
                        
                        {ing.is_removable && (
                          <div className="flex items-center gap-2">
                            <Button
                              variant={ing.show_prominent ? "default" : "outline"}
                              size="sm"
                              onClick={() => updateIngredient(idx, 'show_prominent', !ing.show_prominent)}
                              title="Prominent auf Küchenbon"
                            >
                              {ing.show_prominent ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                            </Button>
                          </div>
                        )}
                        
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => removeIngredient(idx)}
                          className="text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                  
                  {/* Add new ingredient */}
                  <div className="flex gap-2 pt-2 border-t">
                    <Input
                      value={newIngredient}
                      onChange={e => setNewIngredient(e.target.value)}
                      placeholder="Neue Zutat (z.B. Zwiebeln)"
                      className="flex-1"
                      onKeyPress={e => e.key === 'Enter' && addIngredient()}
                    />
                    <Button onClick={addIngredient}>
                      <Plus className="h-4 w-4 mr-1" /> Hinzufügen
                    </Button>
                  </div>
                  
                  {/* Kitchen Bon Preview */}
                  <div className="mt-6">
                    <Label className="flex items-center gap-2 mb-2">
                      <ChefHat className="h-4 w-4" />
                      Vorschau Küchenbon
                    </Label>
                    <KitchenBonPreview />
                  </div>
                </>
              )}
            </TabsContent>
            
            {/* === EXTRAS TAB === */}
            <TabsContent value="extras" className="mt-0 space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Switch 
                    checked={hasExtras}
                    onCheckedChange={setHasExtras}
                    id="has-extras"
                  />
                  <Label htmlFor="has-extras">Hat Extras/Add-ons</Label>
                </div>
              </div>
              
              {hasExtras && (
                <>
                  {/* Existing extras */}
                  {extraGroups.map((group, gIdx) => (
                    <div key={group.group_id} className="border rounded-lg p-4 space-y-2">
                      <div className="font-medium">{group.name}</div>
                      
                      {group.extras.map((extra, eIdx) => (
                        <div 
                          key={extra.extra_id}
                          className="flex items-center gap-2 p-2 bg-gray-50 dark:bg-gray-800 rounded"
                        >
                          <span className="flex-1">{extra.name}</span>
                          <span className="text-sm">
                            {extra.is_free ? 'Gratis' : `+€${extra.price.toFixed(2)}`}
                          </span>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => removeExtra(gIdx, eIdx)}
                            className="h-8 w-8 text-destructive"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  ))}
                  
                  {/* Add new extra */}
                  <div className="flex gap-2 pt-2 border-t">
                    <Input
                      value={newExtra.name}
                      onChange={e => setNewExtra({ ...newExtra, name: e.target.value })}
                      placeholder="Neues Extra (z.B. Extra Käse)"
                      className="flex-1"
                      onKeyPress={e => e.key === 'Enter' && addExtra()}
                    />
                    <Input
                      type="number"
                      step="0.10"
                      value={newExtra.price}
                      onChange={e => setNewExtra({ ...newExtra, price: e.target.value })}
                      placeholder="Preis"
                      className="w-24"
                      onKeyPress={e => e.key === 'Enter' && addExtra()}
                    />
                    <Button onClick={addExtra}>
                      <Plus className="h-4 w-4 mr-1" /> Hinzufügen
                    </Button>
                  </div>
                </>
              )}
            </TabsContent>
          </ScrollArea>
        </Tabs>
        
        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose}>
            Abbrechen
          </Button>
          <Button onClick={handleSave} disabled={loading} data-testid="save-product-btn">
            {loading ? 'Speichern...' : 'Speichern'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
